import React from 'react';
import { AlertTriangle, Info, Star, Target, Clock } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const ReadmeFIRST = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl font-bold">Understanding questions will help you get better results</h1>

        {/* Introduction */}
        <div className="text-gray-800 mb-8 pt-4">
          <p className="text-lg">
            WHAT, WHEN, WHOM all matter during interviews.
          </p>
          <hr className="my-8" />

          <p className="text-md">
            Dear Job Candidate, 
          </p>

          <p className="text-md pt-8">
            I'm Mike Sudermann, the founder of ArcAdvantage.AI. I've been helping candidates just like you get hired for over 20 years.
          </p>

          <p className="text-md pt-8">
            This letter is the most important information in this entire program. Read it carefully. Read it again. Read it again. I can't stress this enough.
          </p>

          <p className="text-md pt-8">
            I've seen it time and time again that people who pay me 3,4,5 thousand dollars and more who follow my advice EXACTLY become the top candidates.</p>
            <p className="text-md pt-8">And those who don't - <i>don't.</i>
          </p>

            <p className="text-md text-gray-800 pt-8">
              The questions below are like living in the Penthouse apartment at the top of the building. 
              This is where the rich people live. Why? Because they make a ton of money. To get to the top you have 
              to be willing to make the climb. Each question you ask the interviewer gets you closer to the top.
            </p>


          {/* Goals Section */}
          <div className="mb-8 pt-8">
            <h2 className="text-3xl mb-4">Interview Goals</h2>
            <div className="pl-4 border-l-2 border-gray-200">
              <p className="mb-2">The goal of any interview is two-fold:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><b>Eliminate the fear of the hiring manager</b> - the fear is making a bad hire</li>
                <li><b>Get a great offer</b> - If it's your first job, it's the highest end of the pay scale offered. If it's your next job, it's an offer that exceeds your previous compensation by at least 12% and ideally closer to 18-20%</li>
              </ol>
            </div>
          </div>

          <p className="text-md text-gray-800 pt-8">
              I have over two decades of executive recruiting expertise and helping candidates 
              position themselves as the #1 Must Hire Candidate regardless of the strength of the competition.
            </p>
        <p className="text-md text-gray-800 pt-8 pb-8">There is only one winner - no participation trophies, no second or third place. ONE WINNER</p>


          {/* Success Stats */}
          <div className="bg-green-50 p-4 rounded-lg mb-8 border border-green-200">
            <p>
              Historically, on average, with my direct customized career and interview advising, my 
              candidates have averaged an 18% increase in compensation with some earning 36%, 44%, 52%, 
              74%, and one saw a 211% increase (results vary by position and company).
            </p>
          </div>

          {/* Warning Callout */}
          <div className="flex gap-3 bg-red-50 p-4 rounded-lg mb-8 border border-red-200">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <p className="font-semibold mb-2">WARNING:</p>
              <p>
                Success comes with a price - you must follow my formula precisely and you must check your 
                EGO at the door. Practice, practice, and practice some more. Ask the questions according to when 
                we suggest you ask them and to whom, and put EMOTION and ENERGY into each question.
              </p>
            </div>
          </div>

          <h2 className="pt-10 text-4xl font-medium mb-2 text-center">People buy Emotion and Energy</h2>
          <p className="text-2xl text-gray-800 pt-8 pb-8 text-center">No body wants to hire a low-energy dud!</p>


          {/* Interview Tip */}
          <div className="flex gap-3 bg-blue-50 p-4 rounded-lg mb-8 border border-blue-200">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
            <div>
              <p className="font-medium mb-2">TIP:</p>
              <p>Take notes throughout the interview so you can use your data collection during the interview and at the close.</p>
            </div>
          </div>

          {/* Interview Request Section */}
          <div className="mb-8">
            <h2 className="text-3xl mb-4 pt-8">Receiving a Phone Call or Email Request to Interview</h2>

              <p className="font-xl pb-8">SITUATION: You receive an interview request for tomorrow (Wednesday) at noon for a position you submitted your resume for.</p>

            
            <div className="flex gap-3 bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
              <Clock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium mb-2">TIP:</p>
                <p>Never agree to an interview less than 24-hours from the company's ask time – you are only available 30 to 36 hours after the original ask date and time.</p>
                <p className="mt-2 font-medium">Here's why:</p>
                <ul className="list-disc pl-6 mt-2">
                  <li>You need time to study your questions (from your InterviewAdvantage report)</li>
                  <li>Research the company and the interviewer(s) (from your InterviewAdvantage report)</li>
                  <li>If it's a video interview you need time to clean up and organize your area. Make sure you have good lighting (test your camera and lighting!)</li>
                  <li>If it's in-person you need time to get your clothes in order and figure out the location and driving time</li>
                </ul>
              </div>
            </div>

            {/* Chess Game Tip */}
            <div className="flex gap-3 bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
              <Target className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium mb-2">TIP: Interviewing is a CHESS GAME</p>
                <p>It is all about strategy and letting a company know you are not available when they want you to interview - they want you and people always want what they don't have - make yourself more valuable by replying back to the email or if on the phone saying this...</p>
                <div className="bg-white p-3 rounded mt-2 text-gray-700">
                  "Thank you for inviting me to interview. I'd love to talk with you and your company but unfortunately, I already have an interview at noon on Wednesday. However, I am available on Thursday from 9:30 to 11 or from 3-4 pm."
                </div>
              </div>
            </div>

            {/* Follow-up Questions */}
            <div className="bg-gray-50 p-4 rounded-lg mb-8">
              <p className="font-medium mb-2">ASK TWO MORE QUESTIONS:</p>
              <ol className="list-decimal pl-6">
                <li>Who will I be interviewing with (full name and title)?</li>
                <li>What are the next steps of the process after this interview?</li>
              </ol>
              <p className="mt-2">YOU NEED THIS INFO FOR YOUR RESEARCH</p>
            </div>
          </div>

          {/* Competition Tip */}
          <div className="mb-8">
            <div className="flex gap-3 bg-yellow-50 p-4 rounded-lg mb-4 border border-yellow-200">
              <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium mb-2">TIP:</p>
                <p>97% of your competition will NOT know how to ask the following question and will spend 100% of their time throwing out all kinds of data points - excessively talking and spouting about accomplishments hoping to cover something the interviewer wants to hear. Essentially a mud slinger. DON'T be a mud slinger. Mud slingers fail 98% of the time.</p>
              </div>
            </div>
          </div>

          {/* Throwing the First Punch Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">THROWING THE FIRST PUNCH ©</h2>
            <p className="mb-4">
              Do you want to fly the plane blind - relying on nothing but hope and prayers? Or ASK the question 
              within the first 2-3 minutes and you get RADAR. You will SEE everything clearly and know your path.
            </p>
            <p className="mb-4">
              You can set yourself up for a 100% chance to WIN. The choice is yours: Guess blindly or act with clarity.
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">WHEN TO ASK:</h3>
                <p>Within the first 2-3 minutes of the interview – before the interviewer asks any questions. You are literally asking the first question.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">WHOM TO ASK: HIRING MANAGER</h3>
                <p className="mb-2">SET UP: You make the statement and ask the question all in one fell swoop – do not pause before going into the question -</p>
                <div className="bg-white p-3 rounded">
                  <p className="font-medium">STATEMENT:</p>
                  <p>"I appreciate you meeting with me today, before we get started, I have quick question."</p>
                  <p className="font-medium mt-2">QUESTION:</p>
                  <p>"Since I would be WORKING DIRECTLY with you, will you please take a quick minute and share with me your expectations of the (insert title) to accomplish in the position."</p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">WHOM TO ASK: ANYONE OTHER THAN HIRING MANAGER (Peer, Subordinate, HR)</h3>
                <div className="bg-white p-3 rounded">
                  <p>"I appreciate you meeting with me today, before we get started, I have quick question, Since I would be INTERFACING with you in this role as the (title of role), will you please take a quick minute and share with me your expectations of the (insert title) to accomplish in the position."</p>
                </div>
              </div>
            </div>
          </div>

          {/* Final Tips */}
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="font-medium mb-2">WHY IS THIS IMPORTANT?</p>
              <p>You will have 100% clarity on what the interviewer wants to hear you talk about. Or you can sling mud - it's your choice.</p>
            </div>

            <div className="flex gap-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium mb-2">TIP:</p>
                <p>Never thank anyone for their time. It's your time too. You are making them more important than you when you say thank you for your time. Instead say, I appreciate you meeting with me.</p>
              </div>
            </div>
          </div>
          <div>
            <p className="text-lg font-medium pt-8">- Mike</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReadmeFIRST;